import { collection, doc, onSnapshot } from "firebase/firestore";
import type { Unsubscribe } from "firebase/firestore";

import { db } from "../firebase/client";
import {
  decodeMember,
  decodeUserDirectory,
  teamCollectionDecoders,
} from "./decoders";
import type {
  ClaimModel,
  MemberModel,
  PlayerModel,
  UserDirectoryModel,
} from "../shared/types/contracts";

type TeamCollectionModels = {
  claims: ClaimModel;
  players: PlayerModel;
};

function identified(
  key: "id" | "uid",
  value: string,
  data: unknown,
): Record<string, unknown> {
  const record =
    typeof data === "object" && data !== null && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {};
  return { [key]: value, ...record };
}

export function observeMembership(
  teamId: string,
  uid: string,
  onMember: (member: MemberModel | null) => void,
  onError: (error: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, "teams", teamId, "members", uid),
    (snapshot) => {
      try {
        onMember(
          snapshot.exists()
            ? decodeMember(identified("uid", uid, snapshot.data()))
            : null,
        );
      } catch (error) {
        onError(error);
      }
    },
    onError,
  );
}

export function observeUserDirectory(
  uid: string,
  onDirectory: (directory: UserDirectoryModel | null) => void,
  onError: (error: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, "users", uid),
    (snapshot) => {
      try {
        onDirectory(
          snapshot.exists()
            ? decodeUserDirectory(identified("uid", uid, snapshot.data()))
            : null,
        );
      } catch (error) {
        onError(error);
      }
    },
    onError,
  );
}

export function observeTeamCollection<Name extends keyof TeamCollectionModels>(
  teamId: string,
  name: Name,
  onDocs: (documents: TeamCollectionModels[Name][]) => void,
  onError: (error: unknown) => void,
): Unsubscribe {
  const decoder = teamCollectionDecoders[name];
  if (!decoder) throw new Error(`Unsupported team collection: ${name}`);
  return onSnapshot(
    collection(db, "teams", teamId, name),
    (snapshot) => {
      try {
        onDocs(
          snapshot.docs.map((entry) =>
            decoder(identified("id", entry.id, entry.data())),
          ) as TeamCollectionModels[Name][],
        );
      } catch (error) {
        onError(error);
      }
    },
    onError,
  );
}
